// src/app/pages/admin/AssetsPage.jsx
import { useEffect, useState } from "react";
import { adminAssets } from "../../../services/adminApi";
import { Search, Filter, Plus, Edit3, Trash2, RefreshCw, Grid3X3, List, MoreVertical, Package, Image as ImageIcon, Tag, Ruler } from "lucide-react";
import AssetDrawer from "./AssetDrawer";
import { FaBox, FaCar, FaChair, FaCube, FaPalette, FaPerson, FaTree } from "react-icons/fa6";

const T = {
  dark: "#0c1118", amber: "#111111", bg: "#f4f6f8",
  surface: "#ffffff", border: "#e8ecf0", muted: "#6b7280",
};

if (typeof document !== "undefined" && !document.getElementById("ap-styles")) {
  const s = document.createElement("style");
  s.id = "ap-styles";
  s.textContent = `
    @keyframes ap-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .ap-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.1) !important; }
    .ap-row:hover td { background: #f4f6f8 !important; }
    .ap-icon-btn:hover { background: #f4f6f8 !important; }
    .ap-menu-item:hover { background: #f4f6f8 !important; }
    .ap-menu-del:hover { background: #fef2f2 !important; color: #dc2626 !important; }
  `;
  document.head.appendChild(s);
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const categories = [
  { value: "", label: "All Categories", icon: FaCube },
  { value: "person",    label: "Person",    icon: FaPerson },
  { value: "vehicle",   label: "Vehicle",   icon: FaCar },
  { value: "tree",      label: "Tree",      icon: FaTree },
  { value: "furniture", label: "Furniture", icon: FaChair },
  { value: "decor",     label: "Decor",     icon: FaPalette },
  { value: "generic",   label: "Generic",   icon: FaBox },
];

export default function AssetsPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedAssets, setSelectedAssets] = useState([]);

  async function load() {
    setLoading(true);
    try {
      const r = await adminAssets.list({ q, category });
      setRows(r.items || []);
      setTotal(r.total || 0);
    } catch (error) {
      console.error("Failed to load assets:", error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [q, category]);

  const handleSelectAsset = (assetId, checked) => {
    setSelectedAssets((prev) => checked ? [...prev, assetId] : prev.filter((id) => id !== assetId));
  };

  const handleSelectAll = (checked) => {
    setSelectedAssets(checked ? rows.map((a) => a.id) : []);
  };

  const handleBulkDelete = async () => {
    if (!selectedAssets.length) return;
    if (!window.confirm(`Delete ${selectedAssets.length} selected assets? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await Promise.all(selectedAssets.map((id) => adminAssets.remove(id)));
      setSelectedAssets([]);
      load();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      alert("Failed to delete some assets. Please try again.");
    } finally { setLoading(false); }
  };

  async function handleDelete(id) {
    if (!window.confirm("Delete this asset? This cannot be undone.")) return;
    try {
      setLoading(true);
      await adminAssets.remove(id);
      await load();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete asset. Please try again.");
    } finally { setLoading(false); }
  }

  const stats = {
    total,
    byCategory: categories.slice(1).map((cat) => ({ ...cat, count: rows.filter((a) => a.category === cat.value).length })),
  };

  const INP = { border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, background: T.surface, fontFamily: "'DM Sans', sans-serif", outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ margin: isMobile ? "0" : "0", padding: isMobile ? "0" : "0" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>3D Assets Library</h1>
            {!isMobile && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Manage your 3D model collection and metadata</p>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={load} disabled={loading} style={{ padding: "8px 10px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer", opacity: loading ? 0.5 : 1 }}>
              <RefreshCw size={16} style={{ display: "block", animation: loading ? "ap-spin 1s linear infinite" : "none" }} />
            </button>
            <button onClick={() => { setEditId(null); setOpen(true); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", background: T.dark, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              <Plus size={16} />{isMobile ? "Add" : "Add Asset"}
            </button>
          </div>
        </div>

        {/* Stats */}
        {isMobile ? (
          <div style={{ overflowX: "auto", display: "flex", gap: 8, marginBottom: 20, paddingBottom: 4 }}>
            <MobileStatTile label="Total" value={stats.total} Icon={Package} />
            {stats.byCategory.map((cat) => (
              <MobileStatTile key={cat.value} label={cat.label} value={cat.count} Icon={cat.icon} />
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 12, marginBottom: 24 }}>
            <StatsCard label="Total Assets" value={stats.total} Icon={Package} />
            {stats.byCategory.map((cat) => (
              <StatsCard key={cat.value} label={cat.label} value={cat.count} Icon={cat.icon} />
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? 14 : 20, marginBottom: 24 }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 12 }}>
            {/* Search */}
            <div style={{ flex: 1, position: "relative" }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
              <input style={{ ...INP, paddingLeft: 40 }} placeholder={isMobile ? "Search assets..." : "Search assets by name, code, or category..."} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            {/* Category */}
            <div style={{ position: "relative", minWidth: isMobile ? "unset" : 180 }}>
              <Filter size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
              <select style={{ ...INP, paddingLeft: 36, appearance: "none" }} value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
              </select>
            </div>
            {/* View Toggle — hidden on mobile */}
            {!isMobile && (
              <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
                {[{ mode: "grid", Icon: Grid3X3 }, { mode: "table", Icon: List }].map(({ mode, Icon }) => (
                  <button key={mode} onClick={() => setViewMode(mode)} style={{ padding: "6px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: viewMode === mode ? T.dark : "transparent", color: viewMode === mode ? "#fff" : T.muted }}>
                    <Icon size={16} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedAssets.length > 0 && (
            <div style={{ marginTop: 16, padding: 14, background: `${T.amber}18`, border: `1px solid ${T.amber}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.dark }}>{selectedAssets.length} asset{selectedAssets.length > 1 ? "s" : ""} selected</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleBulkDelete} style={{ padding: "6px 14px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>Delete Selected</button>
                <button onClick={() => setSelectedAssets([])} style={{ padding: "6px 14px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13, cursor: "pointer" }}>Clear</button>
              </div>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 48, textAlign: "center", color: T.muted, fontSize: 14 }}>
            Loading assets...
          </div>
        )}

        {/* Content */}
        {!loading && rows.length > 0 && (
          isMobile ? (
            <AssetMobileList assets={rows} onEdit={(id) => { setEditId(id); setOpen(true); }} onDelete={handleDelete} />
          ) : viewMode === "grid" ? (
            <AssetGrid assets={rows} selectedAssets={selectedAssets} onSelectAsset={handleSelectAsset} onEdit={(id) => { setEditId(id); setOpen(true); }} onDelete={handleDelete} />
          ) : (
            <AssetTable assets={rows} selectedAssets={selectedAssets} onSelectAsset={handleSelectAsset} onSelectAll={handleSelectAll} onEdit={(id) => { setEditId(id); setOpen(true); }} onDelete={handleDelete} />
          )
        )}

        {/* Empty State */}
        {!loading && rows.length === 0 && (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 48, textAlign: "center" }}>
            <FaCube style={{ margin: "0 auto 16px", color: T.border, display: "block" }} size={48} />
            <div style={{ fontWeight: 600, color: T.dark, marginBottom: 6 }}>No 3D assets found</div>
            <div style={{ color: T.muted, fontSize: 13, marginBottom: 16 }}>
              {q || category ? "Try adjusting your search criteria" : "Get started by adding your first 3D asset"}
            </div>
            {!q && !category && (
              <button onClick={() => { setEditId(null); setOpen(true); }} style={{ padding: "8px 18px", background: T.dark, color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                Add Your First Asset
              </button>
            )}
          </div>
        )}
      </div>

      {/* Asset Drawer */}
      {open && (
        <AssetDrawer id={editId} onClose={async (changed) => { setOpen(false); if (changed) await load(); }} />
      )}
    </div>
  );
}

function MobileStatTile({ label, value, Icon }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 70, flexShrink: 0 }}>
      <Icon size={16} color={T.muted} />
      <div style={{ fontSize: 16, fontWeight: 700, color: T.dark, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: T.muted, textAlign: "center", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}

function StatsCard({ label, value, Icon }) {
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontSize: 11, color: T.muted, marginBottom: 4, fontWeight: 500 }}>{label}</p>
          <p style={{ fontSize: 18, fontWeight: 700, color: T.dark }}>{value}</p>
        </div>
        <Icon size={20} color={T.muted} />
      </div>
    </div>
  );
}

function AssetMobileList({ assets, onEdit, onDelete }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {assets.map((asset) => (
        <AssetMobileCard key={asset.id} asset={asset} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}

function AssetMobileCard({ asset, onEdit, onDelete }) {
  return (
    <div className="ap-card" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", transition: "box-shadow 0.15s" }}>
      {/* Thumbnail */}
      <div style={{ width: 48, height: 48, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {asset.thumbUrl ? (
          <img src={asset.thumbUrl} alt={asset.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <ImageIcon size={22} color={T.border} />
        )}
      </div>
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: T.dark, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{asset.name}</div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans', sans-serif", marginTop: 2 }}>{asset.code}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 3, display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ textTransform: "capitalize" }}>{asset.category}</span>
          <span style={{ color: T.border }}>•</span>
          <span>{asset.placement}</span>
        </div>
      </div>
      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button className="ap-icon-btn" onClick={() => onEdit(asset.id)} title="Edit" style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}>
          <Edit3 size={15} />
        </button>
        <button className="ap-icon-btn" onClick={() => onDelete(asset.id)} title="Delete" style={{ width: 34, height: 34, borderRadius: 8, border: "1px solid #fca5a5", background: "#fef2f2", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626" }}>
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function AssetGrid({ assets, selectedAssets, onSelectAsset, onEdit, onDelete }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
      {assets.map((asset) => (
        <AssetCard key={asset.id} asset={asset} isSelected={selectedAssets.includes(asset.id)} onSelect={onSelectAsset} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </div>
  );
}

function AssetCard({ asset, isSelected, onSelect, onEdit, onDelete }) {
  const [showActions, setShowActions] = useState(false);
  return (
    <div className="ap-card" style={{ background: T.surface, borderRadius: 10, border: `1px solid ${isSelected ? T.amber : T.border}`, boxShadow: isSelected ? `0 0 0 2px ${T.amber}44` : "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden", transition: "box-shadow 0.15s" }}>
      {/* Thumbnail */}
      <div style={{ position: "relative", aspectRatio: "1/1", background: T.bg }}>
        <input type="checkbox" checked={isSelected} onChange={(e) => onSelect(asset.id, e.target.checked)} style={{ position: "absolute", top: 10, left: 10, width: 16, height: 16, zIndex: 10, cursor: "pointer" }} />
        {asset.thumbUrl ? (
          <img src={asset.thumbUrl} alt={asset.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ImageIcon size={48} color={T.border} />
          </div>
        )}
        {/* Menu */}
        <div style={{ position: "absolute", top: 10, right: 10 }}>
          <button onClick={() => setShowActions(!showActions)} className="ap-icon-btn" style={{ width: 30, height: 30, background: T.surface, border: `1px solid ${T.border}`, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
            <MoreVertical size={14} />
          </button>
          {showActions && (
            <div style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", width: 120, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 20, overflow: "hidden" }}>
              <button className="ap-menu-item" onClick={() => onEdit(asset.id)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", fontSize: 13, background: "none", border: "none", cursor: "pointer", color: T.dark }}>
                <Edit3 size={13} /> Edit
              </button>
              <button className="ap-menu-del" onClick={() => onDelete(asset.id)} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>
                <Trash2 size={13} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: "12px 14px" }}>
        <div style={{ fontWeight: 600, color: T.dark, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{asset.name}</div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>{asset.code}</div>
        <div style={{ display: "flex", gap: 12, fontSize: 11, color: T.muted, marginBottom: 8 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Tag size={11} /><span style={{ textTransform: "capitalize" }}>{asset.category}</span></span>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Ruler size={11} />{asset.placement}</span>
        </div>
        <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>
          <div>Native: {asset.native.w?.toFixed(2)} × {asset.native.h?.toFixed(2)} × {asset.native.d?.toFixed(2)}m</div>
          <div>Target: {asset.target.w ?? "–"} × {asset.target.h ?? "–"} × {asset.target.d ?? "–"}ft</div>
        </div>
      </div>
    </div>
  );
}

function AssetTable({ assets, selectedAssets, onSelectAsset, onSelectAll, onEdit, onDelete }) {
  const TH = { padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: T.muted, background: T.bg, borderBottom: `1px solid ${T.border}` };
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={TH}><input type="checkbox" checked={selectedAssets.length === assets.length && assets.length > 0} onChange={(e) => onSelectAll(e.target.checked)} style={{ width: 15, height: 15 }} /></th>
              <th style={TH}>Asset</th>
              <th style={TH}>Code</th>
              <th style={TH}>Category</th>
              <th style={TH}>Placement</th>
              <th style={TH}>Native (W×H×D m)</th>
              <th style={TH}>Target (W×H×D ft)</th>
              <th style={{ ...TH, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((asset) => (
              <AssetTableRow key={asset.id} asset={asset} isSelected={selectedAssets.includes(asset.id)} onSelect={onSelectAsset} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssetTableRow({ asset, isSelected, onSelect, onEdit, onDelete }) {
  const TD = { padding: "12px 16px", fontSize: 13, color: T.dark, borderBottom: `1px solid ${T.border}` };
  return (
    <tr className="ap-row">
      <td style={TD}><input type="checkbox" checked={isSelected} onChange={(e) => onSelect(asset.id, e.target.checked)} style={{ width: 15, height: 15 }} /></td>
      <td style={TD}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {asset.thumbUrl ? (
            <img src={asset.thumbUrl} alt={asset.name} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }} />
          ) : (
            <div style={{ width: 44, height: 44, background: T.bg, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ImageIcon size={18} color={T.border} />
            </div>
          )}
          <div>
            <div style={{ fontWeight: 600 }}>{asset.name}</div>
            <div style={{ fontSize: 11, color: T.muted }}>ID: {asset.id.slice(0, 8)}...</div>
          </div>
        </div>
      </td>
      <td style={{ ...TD, fontFamily: "'DM Sans', sans-serif" }}>{asset.code}</td>
      <td style={TD}>
        <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: T.bg, color: T.muted, textTransform: "capitalize" }}>{asset.category}</span>
      </td>
      <td style={{ ...TD, color: T.muted }}>{asset.placement}</td>
      <td style={{ ...TD, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>{asset.native.w?.toFixed(2)} × {asset.native.h?.toFixed(2)} × {asset.native.d?.toFixed(2)}</td>
      <td style={{ ...TD, fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}>{asset.target.w ?? "–"} × {asset.target.h ?? "–"} × {asset.target.d ?? "–"}</td>
      <td style={{ ...TD, textAlign: "right" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
          <button className="ap-icon-btn" onClick={() => onEdit(asset.id)} title="Edit" style={{ padding: 6, borderRadius: 6, border: "none", background: "none", cursor: "pointer", color: T.muted }}>
            <Edit3 size={15} />
          </button>
          <button className="ap-icon-btn" onClick={() => onDelete(asset.id)} title="Delete" style={{ padding: 6, borderRadius: 6, border: "none", background: "none", cursor: "pointer", color: "#dc2626" }}>
            <Trash2 size={15} />
          </button>
        </div>
      </td>
    </tr>
  );
}
